import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import StaffLayout from "@/components/StaffLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Play, RotateCw, Fuel, Clock, CalendarDays, Wallet, ChevronRight } from "lucide-react";
import { fmtDate, fmtINR } from "@/utils/format";
import { NetBadge } from "@/pages/admin/AdminCash";
import { useProducts } from "@/lib/products";

export default function Dashboard() {
  const products = useProducts();
  const { user } = useAuth();
  const nav = useNavigate();
  const [ctx, setCtx] = useState(null);
  const [current, setCurrent] = useState(undefined);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const load = async () => {
      const [c, s] = await Promise.all([api.get("/business/context"), api.get("/shifts/current")]);
      setCtx(c.data);
      setCurrent(s.data);
    };
    load();
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);

  const shiftLabel = ctx ? `Shift ${ctx.current_shift}` : "—";

  return (
    <StaffLayout>
      <div className="space-y-6">
        <Card className="p-6 rounded-2xl border-slate-200 shadow-sm">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-sm text-slate-500">Welcome back</div>
              <h1 className="text-2xl sm:text-3xl font-display font-bold mt-0.5">{user?.name}</h1>
              <div className="text-sm text-slate-500 mt-0.5">ID · {user?.employee_id}</div>
            </div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--bpcl-orange)]/15 text-[color:var(--bpcl-orange-dark)] px-3 py-1 text-xs font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--bpcl-orange)]" /> {shiftLabel}
            </span>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-6 text-center">
            <Stat icon={CalendarDays} label="Business Date" value={ctx ? fmtDate(ctx.business_date) : "—"} />
            <Stat icon={Clock} label="Current Time" value={now.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })} />
            <Stat icon={Fuel} label="Status" value={current ? "Running" : "Ready"} accent={!!current} />
          </div>
        </Card>

        <Card className="p-6 rounded-2xl border-slate-200 shadow-sm" data-testid="shift-action-card">
          {current === undefined ? (
            <div className="h-24 animate-pulse bg-slate-100 rounded-lg" />
          ) : current ? (
            <div className="space-y-4">
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500 font-medium">Shift in progress</div>
                <h2 className="text-xl font-display font-bold mt-0.5">
                  {current.mpd} · Shift {current.shift_number}
                </h2>
                <div className="text-sm text-slate-500">Started {new Date(current.start_time).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })}</div>
              </div>
              <Button
                onClick={() => nav(`/shift/${current.id}/running`)}
                data-testid="continue-shift-btn"
                className="w-full h-14 text-base rounded-xl bg-[color:var(--bpcl-blue)] hover:bg-[color:var(--bpcl-blue-dark)] font-semibold hover:scale-[1.01] active:scale-[0.99] transition-transform"
              >
                <RotateCw className="w-4 h-4 mr-2" /> CONTINUE SHIFT
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500 font-medium">No shift running</div>
                <h2 className="text-xl font-display font-bold mt-0.5">Ready to start your shift</h2>
                <div className="text-sm text-slate-500">Pick your MPD and enter opening readings.</div>
              </div>
              <Button
                onClick={() => nav("/shift/start")}
                data-testid="start-shift-btn"
                className="w-full h-14 text-base rounded-xl bg-[color:var(--bpcl-blue)] hover:bg-[color:var(--bpcl-blue-dark)] font-semibold hover:scale-[1.01] active:scale-[0.99] transition-transform"
              >
                <Play className="w-4 h-4 mr-2" /> START SHIFT
              </Button>
            </div>
          )}
        </Card>

        <CashCard />

        {ctx?.fuel_prices && (
          <Card className="p-5 rounded-2xl border-slate-200 shadow-sm">
            <div className="text-xs uppercase tracking-wide text-slate-500 font-medium mb-3">Current rates</div>
            <div className="grid grid-cols-3 gap-3">
              {products.map((p, i) => <RateChip key={p.key} name={`${p.short} /${p.unit}`} price={ctx.fuel_prices[p.key]} tone={["blue", "orange", "slate"][i % 3]} />)}
            </div>
          </Card>
        )}
      </div>
    </StaffLayout>
  );
}

function CashCard() {
  const nav = useNavigate();
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/cash/mine").then((r) => setS(r.data.summary)).catch(() => {}); }, []);
  if (!s) return null;
  const tone = s.net < 0 ? "border-red-200 bg-red-50/60" : s.net > 0 ? "border-emerald-200 bg-emerald-50/60" : "border-slate-200";
  return (
    <button className="w-full text-left" onClick={() => nav("/my-shifts?tab=cash")} data-testid="cash-card">
      <Card className={`p-5 rounded-2xl border-2 shadow-sm ${tone}`}>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500 font-medium flex items-center gap-1"><Wallet className="w-3.5 h-3.5" /> Cash account · this month</div>
            <div className="text-xl mt-1"><NetBadge v={s.net} testId="dash-cash-net" /></div>
            <div className="text-xs text-slate-500 mt-1">Shortage {fmtINR(s.shortage)} · Excess {fmtINR(s.excess)} · Discount {fmtINR(s.discount)}</div>
          </div>
          <ChevronRight className="w-5 h-5 text-slate-400" />
        </div>
      </Card>
    </button>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
      <Icon className={`w-4 h-4 mx-auto ${accent ? "text-[color:var(--bpcl-blue)]" : "text-slate-400"}`} />
      <div className="text-[10px] uppercase tracking-wide text-slate-500 mt-1">{label}</div>
      <div className={`text-sm font-semibold mt-0.5 font-mono-num ${accent ? "text-[color:var(--bpcl-blue)]" : ""}`}>{value}</div>
    </div>
  );
}

function RateChip({ name, price, tone }) {
  const tones = {
    blue: "bg-[color:var(--bpcl-blue)]/10 text-[color:var(--bpcl-blue)]",
    orange: "bg-[color:var(--bpcl-orange)]/15 text-[color:var(--bpcl-orange-dark)]",
    slate: "bg-slate-100 text-slate-700",
  };
  return (
    <div className={`rounded-xl p-3 ${tones[tone]}`}>
      <div className="text-[11px] uppercase tracking-wide opacity-70">{name}</div>
      <div className="text-lg font-bold font-mono-num mt-0.5">₹{Number(price).toFixed(2)}</div>
    </div>
  );
}
